/**
 * @component Header
 * @description
 * Componente de cabecera reutilizable para tablas avanzadas.
 * Combina el título del encabezado y una sección de acciones que incluye
 * filtros, botones de creación y acciones masivas.
 *
 * Este componente escucha los cambios en `selectedRowIds` mediante un efecto (`useEffect`)
 * y ejecuta `handleSelectedChange` cada vez que la selección cambia. Esto permite
 * sincronizar el estado de selección de filas con el componente padre.
 *
 * @param {Object} props - Props del componente.
 * @param {Object} props.selectedRowIds - Objeto que contiene los IDs de las filas seleccionadas (por índice).
 * @param {Function} props.handleSelectedChange - Función callback que se ejecuta cuando cambia la selección.
 * @param {string} props.globalFilter - Texto del filtro global de búsqueda.
 * @param {Function} props.setGlobalFilter - Función para actualizar el filtro global.
 * @param {Function} props.handleOpen - Callback que se ejecuta al abrir el modal de creación.
 * @param {Function} props.onBulkAction - Callback que maneja acciones masivas (exportar, eliminar, etc).
 *
 * @returns {JSX.Element} Encabezado de tabla con filtros y acciones disponibles.
 *
 * @example
 * <Header
 *   selectedRowIds={selectedRowIds}
 *   handleSelectedChange={handleSelectedChange}
 *   globalFilter={globalFilter}
 *   setGlobalFilter={setGlobalFilter}
 *   handleOpen={handleOpenModal}
 *   onBulkAction={handleBulkAction}
 * />
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import HeaderTitle from './HeaderTitle';
import ActionsSection from './ActionsSection';

const Header = ({ selectedRowIds, handleSelectedChange, globalFilter, setGlobalFilter, handleOpen, onBulkAction }) => {
	useEffect(() => {
		handleSelectedChange(selectedRowIds);
	}, [selectedRowIds]);

	return (
		<div className="d-lg-flex justify-content-between">
			<HeaderTitle globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
			<div className="border-bottom border-200 my-3"></div>
			<ActionsSection selectedRowIds={selectedRowIds} handleOpen={handleOpen} onBulkAction={onBulkAction} />
		</div>
	);
};

Header.propTypes = {
	selectedRowIds: PropTypes.object,
	handleSelectedChange: PropTypes.func,
	globalFilter: PropTypes.string,
	setGlobalFilter: PropTypes.func,
	handleOpen: PropTypes.func,
	onBulkAction: PropTypes.func
};

export default Header;